import React, { useState, useEffect, useContext, useRef } from 'react';
import { ArrowLeft, Building2, Mail, Phone, FileText, Calendar, AlertCircle, Loader2, CheckCircle, Edit3, X, PlusCircle } from 'lucide-react';
import superAdminServices from '../../../../services/superAdminServices';
import AuthContext from '../../../../context/AuthContext';
import { useParams } from 'react-router-dom';
import { useToast } from '../../../../context/ToastContext';
import CreateOrganisationModal from './CreateOrganisationModal';

export default function AccountRequestDetail({ onBack }) {
  const { requestId } = useParams();
  const { authTokens } = useContext(AuthContext);
  const { showError, showSuccess } = useToast();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [request, setRequest] = useState(null);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    fetchRequestDetail();
  }, [requestId]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowStatusDropdown(false);
        setSelectedStatus(null);
      }
    };

    if (showStatusDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showStatusDropdown]);

  const fetchRequestDetail = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await superAdminServices.getAccountRequestDetail(authTokens.access, requestId);
      console.log(response);
      setRequest(response.data);
    } catch (err) {
      setError(err.message || 'Une erreur est survenue');
      setRequest(null);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrganization = async () => {
    setCreating(true);
    try {
      const result = await superAdminServices.createOrganizationFromRequest(authTokens.access, requestId);
      if (!result.success) throw new Error(result.error);
      setRequest({ ...request, progress_status: 'created', is_organisation_created: true });
      setShowCreateModal(false);
      showSuccess('Organisation créée avec succès. Un email de bienvenue a été envoyé.');
    } catch (err) {
      showError(err.message || "Erreur lors de la création de l'organisation");
    } finally {
      setCreating(false);
    }
  };

  const handleStatusUpdate = async () => {
    if (!selectedStatus || selectedStatus === request.progress_status) {
      setShowStatusDropdown(false);
      setSelectedStatus(null);
      return;
    }

    setUpdating(true);
    try {
      await superAdminServices.updateAccountRequestStatus(
        authTokens.access,
        requestId,
        selectedStatus
      );
      
      // Update local state
      setRequest({ ...request, progress_status: selectedStatus });
      setShowStatusDropdown(false);
      showSuccess("Status updated")
      setSelectedStatus(null);
    } catch (err) {
      showError(err.message || 'Erreur lors de la mise à jour du statut');
    } finally {
      setUpdating(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'pending': {
        color: 'bg-yellow-100 text-yellow-700',
        icon: AlertCircle,
        label: 'En attente'
      },
      'created': {
        color: 'bg-green-100 text-green-700',
        icon: CheckCircle,
        label: 'Créé'
      }
    };

    const config = statusConfig[status] || {
      color: 'bg-gray-100 text-gray-700',
      icon: AlertCircle,
      label: status
    };

    const IconComponent = config.icon;

    return (
      <span className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium ${config.color}`}>
        <IconComponent className="h-4 w-4 mr-2" />
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="p-5 sm:p-6 max-w-4xl mx-auto w-full">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
          <span className="ml-3 text-sm text-gray-600">Chargement des détails...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-5 sm:p-6 max-w-4xl mx-auto w-full">
        <button
          onClick={onBack}
          className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Retour à la liste
        </button>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center">
            <AlertCircle className="h-6 w-6 text-red-600 mr-3" />
            <div>
              <h3 className="text-sm font-semibold text-red-900">Erreur</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!request) {
    return null;
  }

  const statusOptions = [
    { value: 'pending', label: 'En attente', color: 'text-yellow-700', icon: AlertCircle },
    { value: 'created', label: 'Créé', color: 'text-green-700', icon: CheckCircle }
  ];

  return (
    <div className="p-5 sm:p-6 max-w-4xl mx-auto w-full">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={onBack}
          className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Retour à la liste
        </button>
        
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">Détails de la demande</h2>
            <p className="text-sm text-gray-500 mt-1">
              Soumis le {new Date(request.submitted_at).toLocaleDateString('fr-FR', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {getStatusBadge(request.progress_status)}
            {!request.is_organisation_created && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center px-3 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors"
              >
                <PlusCircle className="h-4 w-4 mr-2" />
                Créer l'organisation
              </button>
            )}
            <div className="relative" ref={dropdownRef}>
              {/* <button
                onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                disabled={updating}
                className="inline-flex items-center px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {updating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Mise à jour...
                  </>
                ) : (
                  <>
                    <Edit3 className="h-4 w-4 mr-2" />
                    Modifier le statut
                  </>
                )}
              </button> */}

              {/* Status Dropdown */}
              {showStatusDropdown && (
                <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-semibold text-gray-900">Changer le statut</h4>
                      <button
                        onClick={() => {
                          setShowStatusDropdown(false);
                          setSelectedStatus(null);
                        }}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>

                    <div className="space-y-2">
                      {statusOptions.map((option) => {
                        const IconComponent = option.icon;
                        const isSelected = selectedStatus === option.value;
                        const isCurrent = request.progress_status === option.value;

                        return (
                          <button
                            key={option.value}
                            onClick={() => setSelectedStatus(option.value)}
                            className={`w-full flex items-center px-3 py-2.5 rounded-md text-sm transition-colors ${
                              isSelected
                                ? 'bg-blue-50 border-2 border-blue-500'
                                : isCurrent
                                ? 'bg-gray-50 border border-gray-300'
                                : 'bg-white border border-gray-200 hover:bg-gray-50'
                            }`}
                          >
                            <IconComponent className={`h-4 w-4 mr-2 ${option.color}`} />
                            <span className="font-medium text-gray-900">{option.label}</span>
                            {isCurrent && (
                              <span className="ml-auto text-xs text-gray-500">(actuel)</span>
                            )}
                          </button>
                        );
                      })}
                    </div>

                    <div className="mt-4 pt-3 border-t border-gray-200">
                      <button
                        onClick={handleStatusUpdate}
                        disabled={!selectedStatus || selectedStatus === request.progress_status || updating}
                        className="w-full px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {updating ? 'Mise à jour...' : 'Mettre à jour'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="space-y-6">
        {/* Organisation Information */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <h3 className="text-sm font-semibold text-gray-900">Informations de l'organisation</h3>
          </div>
          <div className="p-6 space-y-4">
            <div className="flex items-start">
              <div className="h-10 w-10 bg-blue-600 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                <Building2 className="h-5 w-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-gray-500">Nom de l'organisation</p>
                <p className="text-base font-medium text-gray-900 mt-1">{request.organisation_name}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-100">
              <div className="flex items-start">
                <Mail className="h-5 w-5 text-gray-400 mr-3 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-500">Email</p>
                  <p className="text-sm text-gray-900 mt-1 break-all">{request.email}</p>
                </div>
              </div>

              <div className="flex items-start">
                <Phone className="h-5 w-5 text-gray-400 mr-3 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-500">Numéro de téléphone</p>
                  <p className="text-sm text-gray-900 mt-1">{request.contact_number}</p>
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-gray-100">
              <div className="flex items-start">
                <FileText className="h-5 w-5 text-gray-400 mr-3 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-500">Nom du service</p>
                  <p className="text-sm text-gray-900 mt-1">{request.service_name}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Reason */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <h3 className="text-sm font-semibold text-gray-900">Raison de la demande</h3>
          </div>
          <div className="p-6">
            <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{request.reason}</p>
          </div>
        </div>

        {/* Metadata */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <h3 className="text-sm font-semibold text-gray-900">Informations supplémentaires</h3>
          </div>
          <div className="p-6 space-y-4">
            <div className="flex items-start">
              <Calendar className="h-5 w-5 text-gray-400 mr-3 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-gray-500">Date de soumission</p>
                <p className="text-sm text-gray-900 mt-1">
                  {new Date(request.submitted_at).toLocaleDateString('fr-FR', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
            </div>

            <div className="flex items-start pt-4 border-t border-gray-100">
              <AlertCircle className="h-5 w-5 text-gray-400 mr-3 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-gray-500">Statut de progression</p>
                <div className="mt-2">{getStatusBadge(request.progress_status)}</div>
              </div>
            </div>

            <div className="flex items-start pt-4 border-t border-gray-100">
              <FileText className="h-5 w-5 text-gray-400 mr-3 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-gray-500">ID de la demande</p>
                <p className="text-sm text-gray-900 mt-1 font-mono">#{request.id}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Create Organisation Confirmation Modal */}
      {showCreateModal && (
        <CreateOrganisationModal
          request={request}
          onConfirm={handleCreateOrganization}
          onClose={() => setShowCreateModal(false)}
          loading={creating}
        />
      )}
    </div>
  );
}